import { DeliveriesService, MealsService } from '@/backend/market'
import { MealRepository } from '../../domain/repositories/MealRepository'
import { MealModel } from '../models/MealModel'
import type { MealFilterEntity } from '../../domain/entities/MealFilterEntity'
import { MealFilterFilterModel } from '../models/MealFilterFilterModel'
import { MealMiniModel } from '../models/MealMiniModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { MealMiniEntity } from '../../domain/entities/MealMiniEntity'
import { MealStatusLogModel } from '../models/MealStatusLogModel'
import type { MealStatusLogEntity } from '../../domain/entities/MealStatusLogEntity'
import { MealItemModel } from '../models/MealItemModel'
import type { MealItemEntity } from '../../domain/entities/MealItemEntity'
import { MealAddressModel } from '../models/MealAddressModel'
import type { MealAddressEntity } from '../../domain/entities/MealAddressEntity'
import { MealUserModel } from '../models/MealUserModel'
import type { MealUserEntity } from '../../domain/entities/MealUserEntity'
import { MealBranchModel } from '../models/MealBranchModel'
import type { MealBranchEntity } from '../../domain/entities/MealBranchEntity'
import { UpdateMealDateModel } from '../models/UpdateMealDateModel'
import type { UpdateMealDateEntity } from '../../domain/entities/UpdateMealDateEntity'
import { MealUpdateStatus } from '../../domain/enums/MealUpdateStatus'
import moment from 'moment'
import type { MealStatus } from '../../domain/enums/MealStatus'
import type { PostMealItemEntity } from '../../domain/entities/PostMealItemEntity'
import { PostMealItemModel } from '../models/PostMealItemModel'
import { useToastStore } from '@/shared/stores/useToastStore'

export class MealRepositoryImpl implements MealRepository {
  async getMeals(
    filterData: MealFilterEntity
  ): Promise<PaginateEntity<MealMiniEntity[]> | { error: true }> {
    const filterModel = new MealFilterFilterModel(filterData)
    try {
      const response = await MealsService.getMealsAdmin(filterModel)
      const mealModels = response.items?.map((item) => new MealMiniModel(item))
      const result = new PaginateEntity<MealMiniModel[]>(response, mealModels)
      return result
    } catch {
      return { error: true }
    }
  }
  async getMeal(id: string): Promise<MealModel | { error: true }> {
    try {
      const response = await MealsService.getMealAdmin({ id })
      const mealModels = new MealModel(response)
      return mealModels
    } catch {
      return { error: true }
    }
  }
  async getMealStatusLogs(id: string): Promise<MealStatusLogEntity[] | { error: true }> {
    try {
      const response = await MealsService.getMealStatusLogsAdmin({ id })
      const mealModels = response.statusLogs?.map((item) => new MealStatusLogModel(item))
      return mealModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getMealItems(id: string): Promise<MealItemEntity[] | { error: true }> {
    try {
      const response = await MealsService.getMealItemsAdmin({ id })
      const mealModels = response.items?.map((item) => new MealItemModel(item))
      return mealModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getMealAddresses(id: string): Promise<MealAddressEntity[] | { error: true }> {
    try {
      const response = await MealsService.getMealAddressesAdmin({ id })
      const mealModels = response.addresses?.map((item) => new MealAddressModel(item))
      return mealModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getMealBranches(id: string): Promise<MealBranchEntity[] | { error: true }> {
    try {
      const response = await MealsService.getMealBranchesAdmin({ id })
      const mealModels = response.branches?.map((item) => new MealBranchModel(item))
      return mealModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getMealUsers(id: string): Promise<MealUserEntity[] | { error: true }> {
    try {
      const response = await MealsService.getMealUsersAdmin({ id })
      const mealModels = response.users?.map((item) => new MealUserModel(item))
      return mealModels ?? []
    } catch {
      return { error: true }
    }
  }
  async availableMealBranchStatus(mealStatus: MealStatus): Promise<MealStatus[] | { error: true }> {
    try {
      const response = await MealsService.getMealStatusCanBeUpdatedToAdmin({ mealStatus })
      return response.mealStatuses ?? []
    } catch {
      return { error: true }
    }
  }
  async updateMealDate(
    id: string,
    data: UpdateMealDateEntity
  ): Promise<{ success: true } | { error: true }> {
    const updateMealDateModel = new UpdateMealDateModel(data)
    try {
      await MealsService.putMealDatesAdmin({ id, requestBody: updateMealDateModel })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async acceptBranchMeal(
    mealBranchId: string,
    selectedPreparationTime: Date
  ): Promise<{ success: true } | { error: true }> {
    try {
      const selectedTime = moment(selectedPreparationTime)
      const now = moment()
      const estimatedPreparationTime = moment().set({
        hour: selectedTime.hour(),
        minute: selectedTime.minute(),
        second: 0,
        millisecond: 0
      })
      if (estimatedPreparationTime.isBefore(now)) estimatedPreparationTime.add(1, 'day')
      await MealsService.postMealBranchAcceptAdmin({
        mealBranchId,
        requestBody: { estimatedPreparationTime: estimatedPreparationTime.toISOString() }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async preparedBranchMeal(mealBranchId: string): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.postMealItemPreparedAdmin({
        mealBranchId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async mealAcquireDriver(
    mealId: string,
    driverId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.postMealAcquireAdmin({
        id: mealId,
        requestBody: {
          driverId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async mealAssignDriver(
    mealId: string,
    driverId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.postMealAssignAdmin({
        id: mealId,
        requestBody: {
          driverId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async mealUnAssignDriver(mealId: string): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.postMealUnassignAdmin({
        id: mealId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateMealStatus(
    mealId: string,
    status: MealUpdateStatus,
    cancelMealReasonId?: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      const toastStore = useToastStore()

      switch (status) {
        case MealUpdateStatus.COLLECTING:
          await MealsService.postMealCollectingAdmin({
            id: mealId
          })
          break
        case MealUpdateStatus.DELIVERING:
          await MealsService.postMealDeliveringAdmin({
            id: mealId
          })
          break
        case MealUpdateStatus.AT_DESTINATION:
          await MealsService.postMealAtDestinationAdmin({
            id: mealId
          })
          break
        case MealUpdateStatus.COMPLETED:
          await MealsService.postMealCompleteAdmin({
            id: mealId
          })
          break
        case MealUpdateStatus.CANCELED:
          await MealsService.postMealCancelAdmin({
            id: mealId,
            requestBody: {
              cancelMealReasonId: cancelMealReasonId!
            }
          })

          break
        case MealUpdateStatus.NOT_DELIVERED:
          await MealsService.postMealNotDeliveredAdmin({
            id: mealId
          })

          break

        default:
          toastStore.updateToastState({
            severity: 'error',
            summary: 'Error',
            detail: 'Invalid status'
          })
          return { error: true }
      }
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async addMealItem(data: PostMealItemEntity): Promise<{ success: true } | { error: true }> {
    try {
      const postMealItemModel = new PostMealItemModel(data)
      await MealsService.postMealItemAdmin({
        requestBody: postMealItemModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateMealItem(
    mealItemId: string,
    quantity: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.putMealItemAdmin({
        mealItemId,
        requestBody: {
          quantity
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteMealItem(mealItemId: string): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.deleteMealItemAdmin({
        mealItemId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async cancelMealDelivery(
    deliveryId: string,
    cancelDeliveryReasonId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await DeliveriesService.postCancelDeliveryAdmin({
        id: deliveryId,
        requestBody: {
          cancelDeliveryReasonId: cancelDeliveryReasonId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
